// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { Form } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";

import { isSet } from "shared/utils/utils";

import SearchFilter from "view/comp/SearchFilter";
import SelectAllCheck from "view/comp/SelectAllCheck";

// longer lists get a search field
const MIN_SEARCH_OPTIONS = 10;

const messages = defineMessages({
  searchHint: {
    id: "Type to filter entries...",
  },
});

export interface ICheckListOption<T extends string | number> {
  value: T;
  label: string;
  // options of a group are listed below a group header; they must be passed consecutively
  group?: string;
}

interface ICheckListProps<T extends string | number> {
  id: string;
  className?: string;
  options: ICheckListOption<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
}

// scrollable checkbox list, replacing multi-select boxes
export default function CheckList<T extends string | number>(props: ICheckListProps<T>) {
  const intl = useIntl();
  const [filterText, setFilterText] = React.useState("");
  const selectedSet = new Set(props.selected);
  const lowerFilterText = filterText.toLocaleLowerCase();
  const hasSearch = props.options.length >= MIN_SEARCH_OPTIONS;
  const shownOptions = props.options.filter((option) =>
    option.label.toLocaleLowerCase().includes(lowerFilterText),
  );

  // keep the order of the options, ignoring selected values that are no option
  const changeSelection = (isSelected: (value: T) => boolean) =>
    props.onChange(props.options.map((option) => option.value).filter(isSelected));

  return (
    <div className={props.className}>
      {hasSearch && (
        <div className="checklist-search">
          <SelectAllCheck
            id={`${props.id}_all`}
            checkedCount={shownOptions.filter((option) => selectedSet.has(option.value)).length}
            totalCount={shownOptions.length}
            onChange={(isChecked) => {
              // (un)check the shown options only
              const shownValues = new Set(shownOptions.map((option) => option.value));
              changeSelection((value) =>
                shownValues.has(value) ? isChecked : selectedSet.has(value),
              );
            }}
          />
          <SearchFilter
            filterText={filterText}
            placeholder={intl.formatMessage(messages.searchHint)}
            onChange={setFilterText}
          />
        </div>
      )}
      <div className="checklist" id={props.id}>
        {shownOptions.map((option, index) => (
          <React.Fragment key={option.value}>
            {isSet(option.group) && option.group !== shownOptions[index - 1]?.group && (
              <div className="checklist-group">{option.group}</div>
            )}
            <Form.Check
              id={`${props.id}_${option.value}`}
              label={option.label}
              checked={selectedSet.has(option.value)}
              onChange={(event) => {
                const isChecked = event.currentTarget.checked;
                changeSelection((value) =>
                  value === option.value ? isChecked : selectedSet.has(value),
                );
              }}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
